import { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import IdentityManagement from "../IdentityManagement/IdentityManagement";
import ConfigurationPage from "../../components/Configuration/ConfigurationPage";
import Coco from "../../pages/Coco";
import DetailsPage from "../../components/DetailsPage/DetailsPage";
import { useSelector } from "react-redux";
import RemoteConsent from "../../pages/coco/RemoteConsent";
import PaperConsent from "../../pages/coco/PaperConsent";
import ProfileUpdate from "../../pages/coco/ProfileUpdate";
import ContentLibrary from "../ContentLibrary/ContentLibrary";
import { isPageRestricted } from "../../features/configSlice";
import {
  PageCoco,
  PageCocoPaperConsent,
  PageCocoProfileUpdate,
  PageCocoRemoteConsent,
  PageConfiguration,
  PageContentLibrary,
  PageFileManagement,
  PageIdentityDetails,
  PageIdentityManagement,
} from "../../utils/controls";
import PageNotFound from "../Page404";
import useTypes from "../../hooks/useTypes";
import AccessDenied from "../AccessDenied/AccessDenied";
import useProfile from "../../hooks/useProfile";
import useCustomRouting from "../../hooks/useCustomRouting";
import {
  isRoleCOCO,
  isValidCocoSource,
} from "../../utils/utils";
import FileManagement from "../FileManagement";
import { getMatchedRoleConfig } from "../../utils/roles";
import { ROLE_CONFIG } from "../../utils/roleConfig";

const AuthRouter = () => {
  const { fetchTypes } = useTypes();
  const { tokenData } = useProfile();
  const roles = tokenData?.roles;
  const roleConfig=getMatchedRoleConfig(roles, ROLE_CONFIG);
  const { redirectToPath } = useCustomRouting();
  const tracker = useSelector((state) => state?.TrackerReducer);
  const { scope } = useSelector((state) => state.controller);
  const {
    controller,
    controllers,
    loading: controllerStatus,
  } = useSelector((state) => state.controllers);
  const { scopeStatus } = useSelector((state) => state.controller);
  const state = useSelector((state) => state.config);
  const { status: configStatus } = state;
  const [baseLoading, setBaseLoading] = useState(true);

  useEffect(() => {
    const loadedStates = ["succeeded", "failed"];
    const controllerLoaded = loadedStates?.includes(controllerStatus);
    const scopeLoaded = loadedStates?.includes(scopeStatus);
    const configLoaded = loadedStates?.includes(configStatus);
    if (controllerLoaded && scopeLoaded && configLoaded) setBaseLoading(false);
    else if (controllerLoaded && controllers?.length === 0)
      setBaseLoading(false);
    else setBaseLoading(true);
  }, [controllerStatus, scopeStatus, configStatus]);

  const isValidCOCOParam = (urlParams) =>
    urlParams &&
    urlParams.get("SourceSystem") &&
    isValidCocoSource(urlParams.get("SourceSystem"));

  const isValidIDDetailsURL = (urlParams) =>
    urlParams &&
    urlParams.get("controller") &&
    urlParams.get("customer_scope") &&
    urlParams.get("type") &&
    urlParams.get("value");

  const isBasePath = (path) => ["/ui/", "/ui"].includes(path);
  const isAccessDeniedPage = (path) => ["/ui/access-denied"].includes(path);
  const handleLoadStartPage = () => {
    redirectToPath(roleConfig.startPage);
  };

  const handleRedirectionWithParams = () => {
    const urlParams = new URLSearchParams(
      window.location.search || localStorage.getItem("urlParams")
    );
    if (isValidCOCOParam(urlParams) || isValidIDDetailsURL(urlParams))
      localStorage.removeItem("urlParams");
    if (isValidCOCOParam(urlParams)) redirectToPath(`/coco/?${urlParams}`);
    else if (
      isValidIDDetailsURL(urlParams) &&
      !isAccessDeniedPage(globalThis.location.pathname)
    )
      redirectToPath(`/details/?${urlParams}`);
    else if (!isBasePath(window.location.pathname))
      redirectToPath(window.location.pathname);
    else if (tracker?.page) {
      if (isBasePath(tracker?.page) && isBasePath(window.location.pathname))
        handleLoadStartPage();
      else if (!isBasePath(tracker?.page)) redirectToPath(tracker?.page);
    } else handleLoadStartPage();
  };

  useEffect(() => {
    if (!baseLoading) {
      fetchTypes(controller, scope);
      handleRedirectionWithParams();
    }
  }, [baseLoading]);
  useEffect(() => {
    if (isRoleCOCO(roles)) {
      handleRedirectionWithParams();
      setBaseLoading(false);
    }
  }, [roles]);

  const checkRolePageRestricted = (page) => {
     return !roleConfig.allowedPages.includes(page) && roleConfig.allowedPages.length > 0;
  };

  const loadRouteElement = (page, element) => {
    if (!isRoleCOCO(roles)) {
      if (baseLoading) return "";
      else if (controller && scope) {
        if (checkRolePageRestricted(page) || isPageRestricted(state, page))
          return <AccessDenied />;
        else return element;
      } else return null;
    } else if (checkRolePageRestricted(page)) return <AccessDenied />;
    else return element;
  };

  return (
    <Routes>
      <Route
        path={PageIdentityManagement}
        element={loadRouteElement(
          PageIdentityManagement,
          <IdentityManagement baseLoading={baseLoading} />
        )}
      />
      <Route
        path={PageConfiguration}
        element={loadRouteElement(
          PageConfiguration,
          <ConfigurationPage
            controllerCodeValue={controller}
            customerScope={scope || "hcp"}
          />
        )}
      />
      <>
        <Route path={PageCoco} element={loadRouteElement(PageCoco, <Coco />)} />
        {
          <Route
            path={PageCocoRemoteConsent}
            element={loadRouteElement(PageCocoRemoteConsent, <RemoteConsent />)}
          />
        }
        {
          <Route
            path={PageCocoPaperConsent}
            element={loadRouteElement(PageCocoPaperConsent, <PaperConsent />)}
          />
        }
        {
          <Route
            path={PageCocoProfileUpdate}
            element={loadRouteElement(PageCocoProfileUpdate, <ProfileUpdate />)}
          />
        }
      </>
      <Route
        path={PageIdentityDetails}
        element={loadRouteElement(
          PageIdentityDetails,
          <DetailsPage
            baseLoading={baseLoading}
            controllerCodeValue={controller}
            customerScope={scope || "hcp"}
          />
        )}
      />
      <Route
        path={PageContentLibrary}
        element={loadRouteElement(
          PageContentLibrary,
          <ContentLibrary
            baseLoading={baseLoading}
            controllerCodeValue={controller}
            customerScope={scope || "hcp"}
          />
        )}
      />
      <Route
        path={PageFileManagement}
        element={loadRouteElement(
          PageFileManagement,
          <FileManagement
            baseLoading={baseLoading}
            controllerCodeValue={controller}
            customerScope={scope || "hcp"}
          />
        )}
      />
      <Route path="/access-denied" element={<AccessDenied />} />
      {!baseLoading && <Route path="/*" element={<PageNotFound />} />}
    </Routes>
  );
};

export default AuthRouter;
